<template>
    <BaseForm
        class="form-login"
        @submit="submitUserLogin"
    >
        <template #title>{{ title }}</template>

        <template #content>
            <FieldValidator
                v-slot="{ field, meta, errors, handleReset }"
                name="email"
            >
                <BaseFormField
                    :required="true"
                    :errors="errors"
                >
                    <BaseInput
                        v-bind="field"
                        type="email"
                        placeholder="E-mail"
                        :validator="meta"
                        @change="errorMessage = null"
                        @input="errorMessage = null"
                        @clear="handleReset"
                    />
                </BaseFormField>
            </FieldValidator>

            <FieldValidator
                v-slot="{ field, meta, errors, handleReset }"
                name="password"
            >
                <BaseFormField
                    :required="true"
                    :errors="errors"
                >
                    <BaseInput
                        v-bind="field"
                        type="password"
                        placeholder="Wachtwoord"
                        :validator="meta"
                        @change="errorMessage = null"
                        @input="errorMessage = null"
                        @clear="handleReset"
                    />
                </BaseFormField>
            </FieldValidator>

            <!-- eslint-disable -->
            <div
                v-if="!!errorMessage"
                class="form__error"
                v-html="errorMessage" 
            />
            <!-- eslint-enable -->
        </template>

        <template #actions>
            <BaseButton
                type="button"
                class="form-login__reset-password"
                @click.prevent="$emit('forgotPassword')"
            >
                Wachtwoord vergeten
            </BaseButton>

            <BaseButton
                :disabled="isSubmitting"
                type="submit"
                class="form__submit button--secondary"
            >
                Inloggen
                <template #after>
                    <BaseIcon icon="arrow-right" />
                </template>
            </BaseButton>
        </template>

        <template #after>
            <slot name="after">
                <strong>
                    {{ globalStore.membershipTexts.becomeMemberCtaLoginModal }}
                </strong>

                <p>
                    {{ globalStore.membershipTexts.becomeMemberCtaLoginModalSmallText }}
                </p>

                <BaseButton
                    :element="NuxtLink"
                    :to="`/${SIGNUP_URL}`"
                    type="button"
                    class="button--secondary"
                    @click="closeAllModals"
                >
                    Lees meer
                    <template #after>
                        <BaseIcon icon="arrow-right" />
                    </template>
                </BaseButton>
            </slot>
        </template>
    </BaseForm>
</template>

<script setup lang="ts">
import { SIGNUP_URL } from '~/constants/membership';
import { LOGIN_INVALID_CREDENTIALS, STATUS } from '~/constants/errorMessages';
import { useForm, Field as FieldValidator } from 'vee-validate';

defineEmits(['forgotPassword']);

defineProps({
    title: {
        type: String,
        default: null
    }
});

const NuxtLink = defineNuxtLink({});

const isSubmitting = ref(false);
const errorMessage = ref<string|null>(null);

const { handleSubmit } = useForm({
    validationSchema: {
        email: 'required|email',
        password: 'required'
    }
});

const userStore = useUserStore();

const { jwtToken } = storeToRefs(userStore);

const { setCookies } = useJwtCookies();

const globalStore = useGlobalStore();
const modalStore = useModalStore();

const ERROR_INVALID_CREDENTIALS = 'invalid_credentials' as const;

const submitUserLogin = handleSubmit(async(formData) => {
    isSubmitting.value = true;

    const { data, error } = await useFetchLaravel<{
        token: string;
    }, {
        message: typeof ERROR_INVALID_CREDENTIALS
    }>(
        '/v1.0/auth/jwt',
        {
            method: 'POST',
            data: formData
        }
    );

    isSubmitting.value = false;

    if (error.value) {
        if (error.value?.data?.message === ERROR_INVALID_CREDENTIALS) {
            errorMessage.value = LOGIN_INVALID_CREDENTIALS;
        } else if (error.value?.status && error.value.status >= 400) {
            errorMessage.value = STATUS['500'];
        }
    } else if (data.value?.token) {
        jwtToken.value = data.value.token;

        await nextTick();

        setCookies(data.value.token);

        await nextTick();

        await refreshNuxtData();

        await nextTick();

        await userStore.fetchUserRelations();

        await nextTick();

        closeLoginModal();
    }
});

const closeLoginModal = () => {
    modalStore.close(MODAL_LOGIN);
    modalStore.close(MODAL_FREE_ACCOUNT_SIGNUP);
};

const closeAllModals = () => {
    modalStore.closeAll();
};
</script>

<style lang="less" src="./FormLogin.less" />
